import {
    Box,
    Icon,
    Tooltip,
    Table,
    CellContentPosition,
    TableColumnData,
    TableRowData,
    FormControlLabel,
    Checkbox,
    Button,
    TableProps,
    IconProps,
    Stack,
    Typography,
} from "@tm/components"
import { useLocalization } from "@tm/localization"
import { Tyre, TyreSelection } from "@bundles/tyres/data/models"
import { ETyresSeason } from "@tm/models"
import { styled } from "@mui/system"
import { extractTyreParameters, seasonDisabled } from "./helper"

type Props = {
    tyreList: Tyre[]
    sameSize: boolean
    viewOnly?: boolean
    scrollable?: boolean
    handleTyreSelect?: (tyreItem: Tyre, season?: ETyresSeason) => void
    handleVariantSelect?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, tyreItem: Tyre, selection: TyreSelection) => void
}
export function TyreSizeTable({ tyreList, sameSize, viewOnly, scrollable, handleTyreSelect, handleVariantSelect }: Props) {
    const { translateText } = useLocalization()

    const renderIcon = () => {
        if (sameSize) {
            return <Icon name="tire" sx={{ fill: "GrayText" }} />
        }
        return (
            <Box display="flex" flexDirection="column">
                <AxleIcon front />
                <AxleIcon />
            </Box>
        )
    }

    const renderTooltip = (tyreItem: Tyre) => {
        const tooltipContent = (front?: boolean) => {
            return (
                <Box display="flex">
                    <Typography size="s">{translateText(1029)}&nbsp;</Typography>
                    <Typography size="s" modifiers={["strong"]}>
                        {front ? tyreItem.rimSizeFront : tyreItem.rimSizeRear}
                    </Typography>
                    <Typography size="s">
                        &nbsp;{" | "}
                        {translateText(1028)}&nbsp;
                    </Typography>
                    <Typography size="s" modifiers={["strong"]}>
                        {front ? tyreItem.rimOffsetFront : tyreItem.rimOffsetRear}
                    </Typography>
                </Box>
            )
        }

        if (sameSize) {
            return (
                <Tooltip title={tooltipContent(true)} variant="light" placement="right" color="primary">
                    <StyledIcon />
                </Tooltip>
            )
        }
        return (
            <Box style={{ display: "flex", flexDirection: "column" }}>
                <Tooltip title={tooltipContent(true)} variant="light" placement="right" color="primary">
                    <StyledIcon />
                </Tooltip>
                <Tooltip title={tooltipContent()} variant="light" placement="right" color="primary">
                    <StyledIcon />
                </Tooltip>
            </Box>
        )
    }

    const renderSameSize = (tyreItem: Tyre, front?: boolean) => {
        if (tyreItem[front ? "seasonSummerFront" : "seasonSummerRear"] && tyreItem[front ? "seasonWinterFront" : "seasonWinterRear"]) {
            tyreItem[front ? "seasonFront" : "seasonRear"] = ETyresSeason.allSeason
        } else if (tyreItem[front ? "seasonSummerFront" : "seasonSummerRear"]) {
            tyreItem[front ? "seasonFront" : "seasonRear"] = ETyresSeason.summer
        } else if (tyreItem[front ? "seasonWinterFront" : "seasonWinterRear"]) {
            tyreItem[front ? "seasonFront" : "seasonRear"] = ETyresSeason.winter
        }

        const [width, ratio, diameter] = extractTyreParameters(tyreItem, front)

        return (
            <Box style={{ display: "flex", alignItems: "center", flex: 1 }}>
                <Box display="flex">
                    <Typography fontWeight={700}>{width}&nbsp;</Typography>
                    <Typography>
                        {`/ ${ratio} ${diameter} `}
                        {tyreItem.loadIndexFront}
                        {tyreItem.speedIndexFront}
                    </Typography>
                </Box>
            </Box>
        )
    }

    const renderCombinationSize = (tyreItem: Tyre) => {
        return (
            <Box style={{ display: "flex", flexDirection: "column" }}>
                {renderSameSize(tyreItem, true)}
                {renderSameSize(tyreItem)}
            </Box>
        )
    }

    const renderSizeSelection = (tyre: Tyre) => {
        return (
            <Box style={{ display: "flex", flexDirection: "column" }}>
                <FormControlLabel
                    label={translateText(1906)}
                    labelPlacement="start"
                    sx={{ ml: 1 }}
                    control={<Checkbox size="small" checked={tyre.selection === "front"} onClick={(e) => handleVariantSelect?.(e, tyre, "front")} />}
                />
                <FormControlLabel
                    label={translateText(1907)}
                    labelPlacement="start"
                    sx={{ ml: 1 }}
                    control={<Checkbox size="small" checked={tyre.selection === "rear"} onClick={(e) => handleVariantSelect?.(e, tyre, "rear")} />}
                />
            </Box>
        )
    }

    const renderSizePosition = () => {
        return (
            <Box style={{ display: "flex", flexDirection: "column" }}>
                <Typography fontSize={16}>{translateText(1906)}</Typography>
                <Typography fontSize={16}>{translateText(1907)}</Typography>
            </Box>
        )
    }

    const renderSeasons = (tyre: Tyre) => {
        return (
            <Stack gap={0.5} direction="row" padding="0 1em">
                <Tooltip title={translateText(800)}>
                    <Button
                        className="tyre-search__frame"
                        startIcon={<Icon name="sun" />}
                        disabled={seasonDisabled(tyre, ETyresSeason.summer, sameSize)}
                        onClick={() => handleTyreSelect?.(tyre, ETyresSeason.summer)}
                    />
                </Tooltip>
                <Tooltip title={translateText(725)}>
                    <Button
                        className="tyre-search__frame"
                        startIcon={<Icon name="all-weather" />}
                        disabled={seasonDisabled(tyre, ETyresSeason.allSeason, sameSize)}
                        onClick={() => handleTyreSelect?.(tyre, ETyresSeason.allSeason)}
                    />
                </Tooltip>
                <Tooltip title={translateText(801)}>
                    <Button
                        className="tyre-search__frame"
                        startIcon={<Icon name="snow" />}
                        disabled={seasonDisabled(tyre, ETyresSeason.winter, sameSize)}
                        onClick={() => handleTyreSelect?.(tyre, ETyresSeason.winter)}
                    />
                </Tooltip>
            </Stack>
        )
    }

    function getColumns() {
        const columns: TableColumnData[] = [
            ...((!viewOnly && [{ alignContent: CellContentPosition.center }]) || []), // icon
            { alignContent: CellContentPosition.left }, // size
            { alignContent: CellContentPosition.left }, // info-icon
            { alignContent: CellContentPosition.right }, // size-selection
            { alignContent: CellContentPosition.center }, // season
        ]

        return columns
    }

    const tyreSizeRows: TableRowData[] = tyreList.map((tyre, index) => ({
        id: index.toString(),
        cells: [
            ...((!viewOnly && [
                {
                    id: sameSize ? "tyre-icon" : "axle-icon",
                    displayValue: renderIcon(),
                },
            ]) ||
                []),
            {
                id: "size",
                displayValue: sameSize ? renderSameSize(tyre, true) : renderCombinationSize(tyre),
            },
            {
                id: "info-icon",
                displayValue: renderTooltip(tyre),
            },
            {
                id: "size-selection",
                displayValue: !sameSize && (viewOnly ? renderSizePosition() : renderSizeSelection(tyre)),
            },

            {
                id: "season",
                displayValue: renderSeasons(tyre),
            },
        ],
    }))

    const onRowClick = (row: TableRowData) => {
        const selectedTyre = tyreList.at(+row.id)
        if (selectedTyre && (sameSize || selectedTyre.selection)) {
            const summerDisabled = seasonDisabled(selectedTyre, ETyresSeason.summer, sameSize)
            handleTyreSelect?.(selectedTyre, summerDisabled ? ETyresSeason.winter : ETyresSeason.summer)
        }
    }

    return (
        <StyledTable
            onRowClick={onRowClick}
            viewOnly={viewOnly}
            scrollbar={scrollable ? "styled" : "no"}
            columns={getColumns()}
            rows={tyreSizeRows}
            rowStyle="uniform"
            headerStyle="hidden"
        />
    )
}

const StyledTable = styled(Table, {
    shouldForwardProp: (prop) => prop !== "viewOny" && prop !== "scrollbar",
})<TableProps & { viewOnly?: boolean }>(({ viewOnly, scrollbar }) => ({
    width: "100%",
    height: scrollbar ? "80%" : "unset",
    "& .Table": {
        gridTemplateColumns: viewOnly ? "max-content auto auto max-content" : "3em max-content auto auto max-content",
        "& .Table-row": {
            ":hover": {
                cursor: viewOnly ? "unset" : "pointer",
            },
        },
    },
}))

const AxleIcon = styled(({ front, ...props }: IconProps & { front?: boolean }) => (
    <Icon name={front ? "axle-car_only_front" : "axle-car_only_back"} {...props} />
))(({ theme }) => ({
    fill: theme.palette.primary.light,
}))

const StyledIcon = styled((props: IconProps) => <Icon name="info" {...props} />)({
    fill: "GrayText",
    ":hover": {
        fill: "currentcolor",
    },
})
